import i18n from '@/i18n';
// Типи й утиліти дашборду портфелю СЕС (Головна, роль CTO/volodp).
// Джерело: GET /scada/overview (весь флот) + погода Open-Meteo (клієнтом).

export interface BessLive {
  powerKw: number | null;
  flow: string | null;              // charge | discharge | idle
  socPct: number | null;
  sohPct: number | null;
  dailyChargeKwh: number | null;
  dailyDischargeKwh: number | null;
}
export interface StationLive {
  assetId: string;
  name: string;
  eicW: string | null;
  capacityKw: number | null;
  curtailmentPct: number | null;
  operationalStatus: string;
  readAtLocal: string | null;
  ageSec: number | null;
  pacKw: number | null;
  ppvKw: number | null;
  availableInverters: number | null;
  expectedInverters: number | null;
  qualityFlag: string | null;
  dailyKwh: number | null;
  latitude: number | null;
  longitude: number | null;
  hasBess: boolean;              // має УЗЕ (asset_bess_systems), незалежно від живих даних
  bess: BessLive | null;         // живі показання УЗЕ (лише де є телеметрія)
}
export interface OverviewResponse { generatedAt: string; tickMs: number; stations: StationLive[] }

export interface StationWeather { tempC: number | null; cloudPct: number | null; code: number | null }

// Погодна умова словом (WMO weather_code → укр.), з фолбеком на хмарність.
export type WxKind = 'sun' | 'partly' | 'cloud' | 'fog' | 'rain' | 'snow' | 'storm';
export function weatherCondition(w?: StationWeather | null): { label: string; kind: WxKind } {
  const c = w?.code;
  if (c != null) {
    if (c === 0) return { label: i18n.t('ak_home_sunny'), kind: 'sun' };
    if (c === 1) return { label: i18n.t('ak_home_mostly_clear'), kind: 'sun' };
    if (c === 2) return { label: i18n.t('ak_home_partly_cloudy'), kind: 'partly' };
    if (c === 3) return { label: i18n.t('ak_home_overcast'), kind: 'cloud' };
    if (c === 45 || c === 48) return { label: i18n.t('ak_home_fog'), kind: 'fog' };
    if (c >= 51 && c <= 57) return { label: i18n.t('ak_home_drizzle'), kind: 'rain' };
    if ((c >= 61 && c <= 67) || (c >= 80 && c <= 82)) return { label: i18n.t('ak_home_rain'), kind: 'rain' };
    if ((c >= 71 && c <= 77) || c === 85 || c === 86) return { label: i18n.t('ak_home_snow'), kind: 'snow' };
    if (c >= 95) return { label: i18n.t('ak_home_thunderstorm'), kind: 'storm' };
  }
  const cl = w?.cloudPct;
  if (cl == null) return { label: '—', kind: 'cloud' };
  if (cl < 20) return { label: i18n.t('ak_home_sunny'), kind: 'sun' };
  if (cl < 60) return { label: i18n.t('ak_home_partly_cloudy'), kind: 'partly' };
  return { label: i18n.t('ak_home_overcast'), kind: 'cloud' };
}

// ── формат ────────────────────────────────────────────────────────────────
export const fmtMw = (kw: number | null | undefined, d = 2) =>
  kw == null || Number.isNaN(kw) ? '—' : (kw / 1000).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'uk-UA', { minimumFractionDigits: d, maximumFractionDigits: d });
export const fmtMwh = (kwh: number | null | undefined, d = 2) =>
  kwh == null || Number.isNaN(kwh) ? '—' : (kwh / 1000).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'uk-UA', { minimumFractionDigits: d, maximumFractionDigits: d });
export const fmtInt = (v: number | null | undefined) =>
  v == null || Number.isNaN(v) ? '—' : Math.round(v).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'uk-UA');
export const fmtPct = (v: number | null | undefined, d = 0) =>
  v == null || Number.isNaN(v) ? '—' : v.toFixed(d);

// Завантаження % (потужність зараз від встановленої).
export const loadPct = (s: StationLive): number | null => {
  if (s.pacKw == null || s.capacityKw == null || s.capacityKw <= 0) return null;
  return Math.max(0, Math.min(100, (s.pacKw / s.capacityKw) * 100));
};

// Статус станції → мітка + колір (догма: ok зелений / warn бурштин / err лише збій).
export function stationStatus(s: StationLive): { label: string; color: string } {
  const stale = s.ageSec != null && s.ageSec > 1800; // >30 хв без свіжих даних
  if (s.qualityFlag === 'error' || (s.expectedInverters && s.availableInverters != null && s.availableInverters === 0))
    return { label: i18n.t('ak_home_alarm'), color: 'var(--err)' };
  if (stale || s.qualityFlag === 'stale') return { label: i18n.t('ak_home_no_data'), color: 'var(--text-dim)' };
  if ((s.curtailmentPct ?? 0) > 0) return { label: i18n.t('ak_home_curtailed'), color: 'var(--warn)' };
  if (s.expectedInverters && s.availableInverters != null && s.availableInverters < s.expectedInverters)
    return { label: i18n.t('ak_home_partial'), color: 'var(--warn)' };
  if (s.operationalStatus === 'active') return { label: i18n.t('ak_home_running'), color: 'var(--ok)' };
  return { label: s.operationalStatus || '—', color: 'var(--text-muted)' };
}

// Одна погодна відповідь Open-Meteo (мультилокація повертає масив, одна — об'єкт).
export async function fetchPortfolioWeather(stations: StationLive[]): Promise<Record<string, StationWeather>> {
  const withCoords = stations.filter((s) => s.latitude != null && s.longitude != null);
  if (!withCoords.length) return {};
  const lat = withCoords.map((s) => s.latitude).join(',');
  const lon = withCoords.map((s) => s.longitude).join(',');
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,cloud_cover,weather_code&timezone=auto`;
  const out: Record<string, StationWeather> = {};
  try {
    const r = await fetch(url);
    if (!r.ok) return {};
    const j = await r.json();
    const arr = Array.isArray(j) ? j : [j];
    withCoords.forEach((s, i) => {
      const cur = arr[i]?.current;
      out[s.assetId] = {
        tempC: cur?.temperature_2m ?? null,
        cloudPct: cur?.cloud_cover ?? null,
        code: cur?.weather_code ?? null,
      };
    });
  } catch { /* погода необов'язкова */ }
  return out;
}
